/**
 * FamilyCostLine — the opened prompt's cost line, shown while a language or
 * place change is pending (lot D step 7, UI-SPEC §2.11):
 *
 *   {nl} langue(s) × {nz} lieu(x) = {nl·nz} prompt(s)[ · {holes} désactivé(s)][ · il reste {n} prompts | · budget atteint]
 *
 * `left` = [B] fitsByAxis[family axis] of GET …/allowances (the server's number);
 * null (allowance -1, agency, figures not loaded) → no « il reste » segment.
 * Amber when the rows the change adds exceed `left` (display only: the
 * button's state is the caller's).
 */
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { atCap } from './variantModel';

export interface FamilyCostLineProps {
  /** familyGrid AFTER the pending change. */
  langs: number;
  places: number;
  holes: number;
  /** Rows the pending change adds. */
  added: number;
  left: number | null;
}

export function FamilyCostLine({ langs, places, holes, added, left }: FamilyCostLineProps) {
  const { t } = useTranslation();
  const amber = left !== null && (atCap(left) || added > left);
  const n = langs * places;
  return (
    <Box sx={{ fontSize: 12, color: amber ? '#b45309' : 'text.secondary', mb: 0.5 }}>
      {t('promptStudio.cost.line', {
        nl: langs,
        langs: t('promptStudio.cost.langue', { count: langs }),
        nz: places,
        places: t('promptStudio.cost.lieu', { count: places }),
        n,
        prompts: t('promptStudio.cost.prompt', { count: n }),
      })}
      {holes > 0 && ` ${t('promptStudio.cost.disabled', { count: holes })}`}
      {left !== null && ` ${atCap(left) ? t('promptStudio.cost.budgetReached') : t('promptStudio.cost.leftOne', { lo: left })}`}
    </Box>
  );
}
